/**
 * APX WorkCenter -- AuthContext
 * Stage 2: DB-backed authentication via tRPC staffAuth.login procedure.
 * Passwords are bcrypt-hashed in the DB; no plaintext credentials in source.
 *
 * Roles: admin | controller | asst_controller | bookkeeping_manager | bookkeeper | tax_division
 *
 * HYDRATION RULE: isLoading is true until the background DB refresh completes.
 * This prevents the router from making redirect decisions (→ blank screen ping-pong)
 * while auth state is still being confirmed. Routes must show <LoadingScreen> when
 * isLoading is true, and only redirect after isLoading becomes false.
 */

import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from "react";
import { trpcClient } from "@/lib/trpc";

export type UserRole = "super_admin" | "admin" | "controller" | "asst_controller" | "bookkeeping_manager" | "bookkeeper_supervisor" | "bookkeeper" | "tax_division";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  initials: string;
  /** true once the user has changed their password at least once — hides the security banner */
  hasChangedPassword?: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isLoading: boolean;
}

const SESSION_KEY = "apx_session";

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Initialize user synchronously from localStorage so there is no blank flash on refresh.
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) return JSON.parse(stored) as AuthUser;
    } catch { /* ignore corrupt storage */ }
    return null;
  });

  // isLoading starts TRUE if there is a cached session that needs background verification.
  // This prevents the router from redirecting before we know if the session is still valid.
  // If there is no cached session, we already know the user is logged out — no loading needed.
  const [isLoading, setIsLoading] = useState(() => {
    try {
      return !!localStorage.getItem(SESSION_KEY);
    } catch {
      return false;
    }
  });

  // Background DB refresh — updates role/name and confirms session validity.
  // Sets isLoading=false when done so the router can make redirect decisions.
  useEffect(() => {
    const refresh = async () => {
      try {
        const stored = localStorage.getItem(SESSION_KEY);
        if (!stored) {
          setIsLoading(false);
          return;
        }
        const cached: AuthUser = JSON.parse(stored);
        try {
          const live = await trpcClient.staffAuth.getRole.query({ email: cached.email });
          if (live.status === 'ok') {
            const refreshed: AuthUser = {
              ...cached,
              role: live.role as UserRole,
              name: live.name,
              initials: live.initials,
              hasChangedPassword: live.hasChangedPassword ?? false,
            };
            setUser(refreshed);
            localStorage.setItem(SESSION_KEY, JSON.stringify(refreshed));
          } else if (live.status === 'deactivated') {
            // Account explicitly deactivated — force logout
            setUser(null);
            localStorage.removeItem(SESSION_KEY);
          }
          // If DB unavailable, keep the cached user — no logout
        } catch {
          // Network error — keep the cached user, no logout
        }
      } catch {
        // Corrupt storage — clear it
        setUser(null);
        localStorage.removeItem(SESSION_KEY);
      } finally {
        // Always mark hydration complete so the router can proceed
        setIsLoading(false);
      }
    };
    refresh();
  }, []);

  // Login via direct trpcClient call — avoids useMutation hook which creates
  // unstable options objects on every render, causing React Error #185 in production.
  const login = useCallback(async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await trpcClient.staffAuth.login.mutate({ email, password });
      if (!result.success) {
        return { success: false, error: result.error ?? "Invalid email or password." };
      }
      const authUser: AuthUser = {
        id: result.user.id,
        name: result.user.name,
        email: result.user.email,
        role: result.user.role as UserRole,
        initials: result.user.initials,
      };
      setUser(authUser);
      localStorage.setItem(SESSION_KEY, JSON.stringify(authUser));
      return { success: true };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Login failed. Please try again.";
      return { success: false, error: message };
    }
  }, []); // stable — uses ref internally

  const loginWithGoogle = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await trpcClient.staffAuth.googleLogin.mutate();
      if (!result.success) return { success: false, error: result.error ?? "Google sign-in failed." };
      const authUser: AuthUser = { id: result.user.id, name: result.user.name, email: result.user.email, role: result.user.role as UserRole, initials: result.user.initials };
      setUser(authUser);
      localStorage.setItem(SESSION_KEY, JSON.stringify(authUser));
      return { success: true };
    } catch {
      return { success: false, error: "Google sign-in failed. Please try again." };
    }
  }, []);

  const logout = useCallback(() => {
    void trpcClient.staffAuth.logout.mutate().catch(() => {
      // Local session clearing still completes if the network is unavailable.
    });
    setUser(null);
    localStorage.removeItem(SESSION_KEY);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, login, loginWithGoogle, logout, isLoading }),
    [user, login, loginWithGoogle, logout, isLoading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * Update own password via tRPC (Stage 2).
 * Called from ChangePasswordModal — requires the current password to verify identity.
 * Returns a promise so the modal can await and show feedback.
 */
export async function updatePasswordDB(
  currentEmail: string,
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> {
  // Verify current password first via login endpoint
  try {
    const { trpcClient } = await import("@/lib/trpc");
    const verify = await trpcClient.staffAuth.login.mutate({ email: currentEmail, password: currentPassword });
    if (!verify.success) return { success: false, error: "Incorrect current password." };
    await trpcClient.staffManagement.resetPassword.mutate({
      staffId: Number(verify.user.id),
      newPassword,
      callerEmail: currentEmail, // user resetting their own password
    });
    // Refresh the cached session to mark hasChangedPassword = true so the banner hides immediately
    const SESSION_KEY = "apx_session";
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        const cached = JSON.parse(stored);
        localStorage.setItem(SESSION_KEY, JSON.stringify({ ...cached, hasChangedPassword: true }));
      } catch { /* ignore */ }
    }
    return { success: true };
  } catch {
    return { success: false, error: "Password update failed. Please try again." };
  }
}
